import { createNode, fadeIn, fadeOut } from '../utils/dom';
import { translate, charsetEscape, detectLanguage } from '../utils/translate';
import { log, logMethodCall } from '../utils/debug';
import Event from '../const/event';
import {
  DAY, DEFAULT_LANGUAGE, KEY_HAS_UNREAD_NOTIFICATIONS, HOURS, KEY_SPEECHBUBBLE_UNREAD_NOTIFICATIONS, MINUTES, PANEL_TABS, KEY_UNREAD_NOTIFICATIONS_COUNT
} from '../const/common';
import { getIconUrl } from '../utils/icons';

const WEEK_DAY = 8;
const MIN_TOP_POSITION = 150;
const INT_RADIX = 10;

export default class Panel {
  constructor(config, subscriptionManager, triggerEvent, api) {
    this.config = config;
    this.subscriptionManager = subscriptionManager;
    this.triggerEvent = triggerEvent;
    this.api = api;
    this.localization = this.getLocalization();
    this.language = detectLanguage(config);
    require('./panel.scss');
    this.tab = config.triggerType || (config.hidePanelNotifications ? PANEL_TABS.SETTINGS : PANEL_TABS.NOTIFICATIONS);
  }

  show() {
    this.checkPanelOpen('.cleverpush-inline-panel');
    if (!this.panel) {
      let className = 'cleverpush-panel';
      className += ` cleverpush-panel-position-${this.config.notificationBellPosition || 'left'}`;

      document.querySelector('body').appendChild(createNode(
        `<div class="cleverpush-panel-overlay"></div><div class="${className}">`
        + '<div class="cleverpush-panel-content"></div>'
        + '</div>',
      ));
    }

    this.panel = document.querySelector('.cleverpush-panel');
    this.panelOverlay = document.querySelector('.cleverpush-panel-overlay');
    this.bindPanelOverlay();
    this.showPanelContent();

    this.triggerEvent(Event.PANEL_SHOWN);

    if (this.api) {
      this.api.trackPanel(null, 'opened');
    }
  }

  checkPanelOpen(className) {
    const panel = document.querySelector(className);
    if (panel) {
      panel.remove();
      this.hide();
    }
  }

  isPanelGoingOutsideOfWindow(linkPosition) {
    return linkPosition && linkPosition.right > window.innerWidth / 1.5;
  }

  parseStyleToNumber(style) {
    try {
      const intSpace = parseInt(style, INT_RADIX);
      if (intSpace < 0) {
        return 0;
      }

      return intSpace;
    } catch (error) {
      return style;
    }
  }

  showSpeechbubblePanel(specialLink) {
    this.bubbleSpeechLink = specialLink;
    this.checkPanelOpen('.cleverpush-panel');
    if (!this.panel) {
      const className = 'cleverpush-inline-panel';
      document.querySelector('body').appendChild(createNode(
        `<div class="cleverpush-panel-overlay"></div> <div class="${className}">`
        + '<div class="cleverpush-panel-content"></div>'
        + '</div>',
      ));
    }

    this.panel = document.querySelector('.cleverpush-inline-panel');
    const linkPosition = specialLink.getBoundingClientRect();
    const linkStyles = window.getComputedStyle(specialLink);
    const panelStyles = window.getComputedStyle(this.panel);
    const paddingInline = ((this.parseStyleToNumber(linkStyles.paddingLeft) || 0) + (this.parseStyleToNumber(linkStyles.marginLeft) || 0) + (this.parseStyleToNumber(linkStyles.paddingRight) || 0) + (this.parseStyleToNumber(linkStyles.marginRight) || 0));
    const paddingBlock = ((this.parseStyleToNumber(linkStyles.paddingTop) || 0) + (this.parseStyleToNumber(linkStyles.marginTop) || 0) + (this.parseStyleToNumber(linkStyles.paddingBottom) || 0) + (this.parseStyleToNumber(linkStyles.marginBottom) || 0));
    const hasPaddingBlock = paddingBlock > 0;

    const extraBlockSpace = hasPaddingBlock ? 1 : 2;
    const spaceToLeaveForLeftBubble = 8;
    const spaceToLeaveForRightBubble = 85;

    const elementConsumedSpace = {
      height: this.parseStyleToNumber(linkStyles.height) * extraBlockSpace,
      width: this.parseStyleToNumber(linkStyles.width) - paddingInline,
      spacingLeft: this.parseStyleToNumber(linkStyles.paddingLeft) + this.parseStyleToNumber(linkStyles.marginLeft),
      spacingRight: this.parseStyleToNumber(linkStyles.paddingRight) + this.parseStyleToNumber(linkStyles.marginRight),
      spacingTop: this.parseStyleToNumber(linkStyles.paddingTop) + this.parseStyleToNumber(linkStyles.marginTop),
      spacingBottom: this.parseStyleToNumber(linkStyles.paddingBottom) + this.parseStyleToNumber(linkStyles.marginBottom),
    };

    if (linkPosition.top < MIN_TOP_POSITION) {
      this.panel.style.position = 'fixed';
      document.body.style.overflow = 'hidden';
      this.panel.style.top = `${linkPosition.top + elementConsumedSpace.height}px`;
    } else {
      this.panel.style.position = 'absolute';
      document.body.style.overflow = 'auto';
      this.panel.style.top = `${linkPosition.top + window.scrollY + elementConsumedSpace.height}px`;
    }

    if (this.isPanelGoingOutsideOfWindow(linkPosition)) {
      this.panel.style.left = 'initial';
      const spaceFromRight = window.innerWidth - linkPosition.right - elementConsumedSpace.width;
      this.panel.style.right = `${spaceFromRight}px`;
      const spaceFromRightForArrow = (this.parseStyleToNumber(panelStyles.width) * spaceToLeaveForRightBubble) / 100;
      this.panel.style.setProperty('--left', `${spaceFromRightForArrow}px`);
    } else {
      const removalSpace = elementConsumedSpace.width <= 35 ? (elementConsumedSpace.width) : 0;
      const spaceFromRight = (linkPosition.left + window.scrollX + elementConsumedSpace.spacingLeft) - removalSpace;
      this.panel.style.left = `${spaceFromRight}px`;
      const spaceFromLeftForArrow = (this.parseStyleToNumber(panelStyles.width) * spaceToLeaveForLeftBubble) / 100;
      this.panel.style.setProperty('--left', `${spaceFromLeftForArrow}px`);
    }
    fadeIn(this.panel, 200);
    this.panelOverlay = document.querySelector('.cleverpush-panel-overlay');
    this.bindPanelOverlay();
    this.showPanelContent();
    this.triggerEvent(Event.PANEL_SHOWN);

    if (this.api) {
      this.api.trackPanel(null, 'opened');
    }
  }

  bindPanelOverlay() {
    if (this.panelOverlay) {
      this.panelOverlay.addEventListener('click', () => this.hide());
    }
  }

  hide() {
    if (this.panel && this.panel.parentNode) {
      this.panel.parentNode.removeChild(this.panel);
    }

    if (this.panelOverlay) {
      this.panelOverlay.parentNode.removeChild(this.panelOverlay);
      document.body.style.overflow = 'auto';
    }

    this.panel = null;
    this.closeButton = null;
    this.footerCloseButton = null;
    this.tabButtons = null;
    this.panelOverlay = null;

    this.triggerEvent(Event.PANEL_HIDDEN);
  }

  onCloseClick(e) {
    e.preventDefault();

    this.hide();
  }

  onUnsubscribeClick(e) {
    if (confirm(this.localization.confirm)) {
      this.onCloseClick(e);
      this.subscriptionManager.unsubscribe();
    }
  }

  setTab(tab) {
    this.tab = tab;
    this.showPanelContent();
  }

  onTabButtonClick(e) {
    logMethodCall('onTabButtonClick', e.currentTarget.dataset.tab);
    e.preventDefault();
    this.setTab(e.currentTarget.dataset.tab || 'notifications');
  }

  openNotification(e) {
    e.preventDefault();
    window.open(e.currentTarget.dataset.url, '_blank');
    this.subscriptionManager.storageManager.setNotificationRead(e.currentTarget.dataset.id);
    const isUnread = Boolean(e.currentTarget.dataset.read);
    if (isUnread) {
      this.updateNotificationCount();
    }
    this.showPanelContent();
    if (this.api) {
      this.api.trackPanel(e.currentTarget.dataset.id, 'clicked');
    }
  }

  updateNotificationCount() {
    let notificationCount = Number(localStorage.getItem(KEY_UNREAD_NOTIFICATIONS_COUNT));
    if (notificationCount && this.bubbleSpeechLink) {
      const countElelement = this.bubbleSpeechLink.firstChild;
      notificationCount -= 1;
      if (!notificationCount) {
        this.bubbleSpeechLink.removeChild(countElelement);
        this.bubbleSpeechLink.classList.remove(KEY_HAS_UNREAD_NOTIFICATIONS);
        return;
      }
      countElelement.innerText = notificationCount;
    }
  }

  getLocalization() {
    let configBellLocalization = this.config.bellLocalization || {};
    const language = detectLanguage(this.config || {});
    if (this.config.bellLocalizationTranslations && language && this.config.bellLocalizationTranslations[language]) {
      configBellLocalization = { ...configBellLocalization, ...this.config.bellLocalizationTranslations[language] };
    }

    const localization = {
      notifications: charsetEscape(configBellLocalization.notifications) || translate('panel.notifications'),
      settings: charsetEscape(configBellLocalization.settings) || translate('panel.settings'),
      editTopics: charsetEscape(configBellLocalization.editTopics) || translate('panel.editTopics'),
      notificationEmpty: charsetEscape(configBellLocalization.notificationEmpty) || translate('panel.notificationsEmpty'),
      save: charsetEscape(configBellLocalization.save) || translate('panel.save'),
      close: charsetEscape(configBellLocalization.close) || translate('panel.close'),
      successfulSave: charsetEscape(configBellLocalization.successfulSave) || translate('panel.successfullySaved'),
      unSubscribeNotification: charsetEscape(configBellLocalization.unSubscribeNotification) || translate('unsubscribe.title'),
      confirm: charsetEscape(configBellLocalization.confirm) || translate('unsubscribe.confirm'),
    };

    return localization;
  }

  timeString(date) {
    let result = date.toISOString().substring(0, 10);
    result += ' ';
    let hours = date.getHours();
    if (hours < 10) {
      hours = `0${hours}`;
    }
    let minutes = date.getMinutes();
    if (minutes < 10) {
      minutes = `0${minutes}`;
    }
    result += `${hours}:${minutes}`;
    return result;
  }

  checkDateHour(date) {
    return new Date() - date < HOURS;
  }

  checkIsToday(date) {
    const today = new Date(Date.now()).setUTCHours(0, 0, 0, 0);
    if (date === today) {
      return true;
    }
    return false;
  }

  checkIsYesterday(date) {
    const yesterday = new Date(Date.now() - DAY).setUTCHours(0, 0, 0, 0);
    if (date === yesterday) {
      return true;
    }
    return false;
  }

  getDaysDifference(date) {
    return Math.abs(new Date() - Date.parse(date)) / DAY;
  }

  notificationDay = (notificationDate) => {
    const timestamp = Date.parse(notificationDate);
    const date = new Date(timestamp).setUTCHours(0, 0, 0, 0);
    if (this.checkIsToday(date) || this.checkIsYesterday(date)) {
      return this.getTodayAndYesterday(date);
    }

    if (this.getDaysDifference(notificationDate) > WEEK_DAY) {
      return new Date(date).toLocaleString(this.language || DEFAULT_LANGUAGE, { dateStyle: 'long' });
    }

    return new Date(date).toLocaleString(this.language || DEFAULT_LANGUAGE, { weekday: 'long' });
  };

  groupToDay = (day, notifications) => ({
    day,
    notifications: notifications[day]
  });

  groupBy = (notifications) => {
    const initialValue = {};
    return notifications.reduce((acc, currentVal) => {
      const myAttribute = this.notificationDay(currentVal.sentAt);
      acc[myAttribute] = [...(acc[myAttribute] || []), currentVal];
      return acc;
    }, initialValue);
  };

  getTimeAgo(timestamp) {
    const date = new Date(timestamp).setUTCHours(0, 0, 0, 0);
    const rtf = new Intl.RelativeTimeFormat(this.language || DEFAULT_LANGUAGE, { numeric: 'auto' });
    const dayOrHour = this.checkIsToday(date) || this.checkIsYesterday(date) ? HOURS : DAY;
    const difference = Math.round((timestamp - Date.now()) / dayOrHour);
    if (this.checkDateHour(date)) {
      const minutesDiff = Math.round((timestamp - Date.now()) / MINUTES);
      return rtf.format(minutesDiff, 'minutes');
    }

    if (this.checkIsToday(date) || this.checkIsYesterday(date)) {
      return rtf.format(difference, 'hours');
    }
    return rtf.format(difference, 'day');
  }

  getTodayAndYesterday(date) {
    const rtf = new Intl.RelativeTimeFormat(this.language || DEFAULT_LANGUAGE, { numeric: 'auto' });

    const difference = Math.trunc((date - Date.now()) / DAY);
    return rtf.format(difference, 'day');
  }

  showPanelContent() {
    const panelContent = this.panel.querySelector('.cleverpush-panel-content');
    const hidePanelNotifications = this.config.hidePanelNotifications;
    const gearSettingOption = this.config.bellPanelGearButtonEnabled;
    let content = '';
    if (this.config.bellPanelTitle) {
      content += `<div class="cleverpush-panel-title-section"><div class="cleverpush-title">${this.config.bellPanelTitle}</div><button class="cleverpush-panel-close" type="button">&times;</button></div>`;
    }
    content += !hidePanelNotifications && !gearSettingOption ? `<div class="cleverpush-panel-buttons">
      <button type="button" class="cleverpush-panel-button${this.tab === 'notifications' ? ' active' : ''}" data-tab="notifications">${this.localization.notifications}</button>`
      + `<button type="button" class="cleverpush-panel-button${this.tab === 'settings' ? ' active' : ''}" data-tab="settings">${this.localization.settings}</button>`
      + '</div>' : '';

    if (this.tab === 'notifications') {
      content += gearSettingOption ? `<div class="gear-setting-options"><div>${this.localization.notifications}</div> <img data-tab="settings" class"cleverpush-panel-icon-button" src=${getIconUrl('setting')} /> </div>` : '';
    } else {
      content += gearSettingOption ? `<div class="gear-setting-options"><div>${this.localization.settings}</div> <img data-tab="notifications" class"cleverpush-panel-icon-button" src=${getIconUrl('back')} /> </div>` : '';
    }

    if (this.config.notificationBellColor) {
      content += `<style>.cleverpush-panel-button.active, .cleverpush-panel-settings button, .cleverpush-panel-settings button:hover, .cleverpush-panel-footer button, .cleverpush-panel-footer button:hover { background-color: ${this.config.notificationBellColor}; }</style>`;
    }

    if (!this.panel.classList.contains('cleverpush-inline-panel') && !this.config.bellPanelHeaderHidden) {
      content += `
      <div class="cleverpush-panel-header" style="border-bottom-color: ${this.config.notificationBellColor || ''}"><img src="${this.config.channelIcon}"></div>
      `;
    }

    const setPanelContent = (newContent) => {
      panelContent.innerHTML = `${newContent}<div class="cleverpush-panel-footer"><button type="button" class="cleverpush-panel-footer-close">${this.localization.close}</button><div class="cleverpush-panel-branding">${this.getBrandingLinks()}</div></div>`;

      this.footerCloseButton = this.panel.querySelector('.cleverpush-panel-footer-close');
      if (this.footerCloseButton) {
        this.footerCloseButton.addEventListener('click', this.onCloseClick.bind(this));
      }

      this.tabButtons = !gearSettingOption ? this.panel.querySelectorAll('.cleverpush-panel-button') : this.panel.querySelectorAll('.gear-setting-options img');
      for (let i = 0; i < this.tabButtons.length; i += 1) {
        const tabButton = this.tabButtons[i];
        tabButton.addEventListener('click', this.onTabButtonClick.bind(this));
      }

      this.notificationItems = this.panel.querySelectorAll('.cleverpush-panel-notification');
      for (let i = 0; i < this.notificationItems.length; i += 1) {
        const notification = this.notificationItems[i];
        notification.addEventListener('click', this.openNotification.bind(this));
      }

      this.closeButton = this.panel.querySelector('.cleverpush-panel-close');
      if (this.closeButton) {
        this.closeButton.addEventListener('click', this.onCloseClick.bind(this));
      }
    };

    if (this.tab === 'notifications') {
      content += '<div class="cleverpush-panel-spinner"><div class="spinner">\n'
        + '                <div class="double-bounce1"></div>\n'
        + '                <div class="double-bounce2"></div>\n'
        + '            </div></div>';
      setPanelContent(content);
      this.subscriptionManager.getStoredNotifications().then((notifications) => {
        if (this.tab !== 'notifications') {
          // user already switched tab fast enough
          return;
        }
        this.subscriptionManager.storageManager.checkUnreadNotifications(notifications);
        const groupNotifications = this.groupBy(notifications);
        let unreadNotificationsCount = 0;
        const result = Object.keys(groupNotifications).map((day) => this.groupToDay(day, groupNotifications)).sort((item) => item.notifications.sentAt);
        this.subscriptionManager.storageManager.deleteOldNotificationsIds(notifications);
        if (notifications.length) {
          content += '<div class="cleverpush-panel-notifications" style="display: none;">';
          result.forEach((item) => {
            content += `<div class="cleverpush-panel-day">${item.day}</div>`;
            item.notifications.slice(0, 50).forEach((notification) => {
              if (notification.testVariants && notification.testVariants.length && notification.testVariants[0]) {
                if (!notification.title && notification.testVariants[0].title) {
                  notification.title = notification.testVariants[0].title;
                }
                if (!notification.text && notification.testVariants[0].text) {
                  notification.text = notification.testVariants[0].text;
                }
                if (notification.testVariants[0].url) {
                  notification.url = notification.testVariants[0].url;
                }
              }

              const date = new Date(Date.parse(notification.sentAt || notification.queuedAt || notification.scheduledAt || notification.createdAt));
              const isRead = this.subscriptionManager.storageManager.isNotificationRead(notification._id);

              if (!isRead && this.panel.classList.contains('cleverpush-inline-panel')) {
                unreadNotificationsCount += 1;
              }

              content += `<div class="cleverpush-panel-notification ${isRead ? 'cleverpush-has-read' : 'cleverpush-has-unread'} ${this.checkDateHour(date) ? 'cleverpush-notification-current-hour' : ''}" data-read=${isRead} data-url="${notification.url}" data-id="${notification._id}" style="border-left: ${isRead ? 'none' : `3px solid ${this.config.notificationBellColor || 'red'};`}">`;

              content += `<div class="cleverpush-panel-notification-image" style="background-image: url('${notification.mediaUrl || notification.iconUrl || this.config.channelIcon}');"></div>`;

              content += `<div class="cleverpush-panel-notification-title">${notification.title}</div>`;
              if (notification.text) {
                if (notification.text.length > 100) {
                  notification.text = `${notification.text.substr(0, 100)}…`;
                }
                content += `<div class="cleverpush-panel-notification-text">${notification.text}</div>`;
              }
              if (notification.sentAt || notification.createdAt) {
                const timestamp = Date.parse(notification.sentAt || notification.queuedAt || notification.scheduledAt || notification.createdAt);
                content += `<div class="cleverpush-panel-notification-time" style="color: ${isRead ? '' : this.config.notificationBellColor || ''}">${this.getTimeAgo(timestamp) || ''}</div>`;
              }
              content += '</div>';
              content += '<div class="line"></div>';
            });
          });
          content += '</div>';
        } else {
          content += `<div class="cleverpush-panel-notifications-empty">${this.localization.notificationEmpty}</div>`;
        }

        if (!unreadNotificationsCount && this.bubbleSpeechLink) {
          this.bubbleSpeechLink.classList.remove(KEY_HAS_UNREAD_NOTIFICATIONS);
          sessionStorage.setItem(KEY_SPEECHBUBBLE_UNREAD_NOTIFICATIONS, false);
        }

        setPanelContent(content);

        const spinner = panelContent.querySelector('.cleverpush-panel-spinner');
        const notificationsDiv = panelContent.querySelector(notifications.length ? '.cleverpush-panel-notifications' : '.cleverpush-panel-notifications-empty');
        if (spinner && notificationsDiv) {
          fadeOut(spinner, 150, () => {
            spinner.style.display = 'none';
            notificationsDiv.style.opacity = 0;
            notificationsDiv.style.display = 'block';
            fadeIn(notificationsDiv, 150);
          });
        }
      });
    } else if (this.tab === 'settings') {
      let topicsContent;

      const setSettingsContent = () => {
        content += `${`<div class="cleverpush-panel-settings">${topicsContent || ''

        }<div class="cleverpush-panel-unsubscribe">`
          + '<button type="button" class="cleverpush-panel-unsubscribe-button">'}${this.localization.unSubscribeNotification}</button>`
          + '</div>'
          + '</div>';

        setPanelContent(content);

        this.unsubscribeButton = this.panel.querySelector('.cleverpush-panel-unsubscribe-button');
        if (this.unsubscribeButton) {
          this.unsubscribeButton.addEventListener('click', this.onUnsubscribeClick.bind(this));
        }

        if (topicsContent) {
          if (this.config.topicsTemplateFunction && topicsContent) {
            const topicsDiv = panelContent.querySelector('.cleverpush-topics');
            if (topicsDiv) {
              try {
                const templateFunc = eval(`(${this.config.topicsTemplateFunction})`);
                if (typeof templateFunc === 'function') {
                  this.subscriptionManager.getTopics().then((selectedTopicIds) => {
                    templateFunc(topicsDiv, this.config, selectedTopicIds, 'panel');
                    this.topicCheckboxes = this.panel.querySelectorAll(`.cleverpush-panel-topics input[type="${this.config.confirmAlertRadioButtonTopics ? 'radio' : 'checkbox'}"]`);
                  });
                }
              } catch (err) {
                log.error(err);
              }
            }
          } else {
            this.topicCheckboxes = this.panel.querySelectorAll(`.cleverpush-panel-topic-selection input[type="${this.config.confirmAlertRadioButtonTopics ? 'radio' : 'checkbox'}"]`);
          }

          this.saveTopicsButton = this.panel.querySelector('.cleverpush-panel-save-topics-button');
          if (this.saveTopicsButton) {
            this.saveTopicsButton.addEventListener('click', this.saveTopics.bind(this));
          }
        }
      };

      if (/*! this.config.confirmAlertHideChannelTopics && */this.config.channelTopics && this.config.channelTopics.length) {
        if (this.config.topicsTemplateFunction) {
          topicsContent = `<div><h3>${this.localization.editTopics}</h3><div class="cleverpush-topics cleverpush-panel-topics"></div><div class="cleverpush-panel-save-topics"><button type="button" class="cleverpush-panel-save-topics-button">${this.localization.save}</div></button>`;
          setSettingsContent();
        } else {
          this.subscriptionManager.getTopics().then((selectedTopicIds) => {
            const topics = this.config.channelTopics.filter((topic) => !topic.parentTopic);

            topicsContent = `${`${'<div class="cleverpush-topics cleverpush-panel-topics">'
              + '<h3>'}${this.localization.editTopics}</h3><div class="cleverpush-panel-topic-selection">`}${topics.sort((a, b) => a.sort - b.sort).map((topic) => {
              const topicName = charsetEscape(topic.name);
              const topicDescription = topic.description ? `<div class="cleverpush-topic-description">${charsetEscape(topic.description)}</div>` : '';
              const childTopics = this.config.channelTopics.filter((childTopic) => childTopic.parentTopic === topic._id);

              let topicSelection = `<div><div class="cleverpush-panel-topic cleverpush-topic-${topic._id} ${childTopics.length ? 'topic-has-children' : ''}"><label><input type="${this.config.confirmAlertRadioButtonTopics ? 'radio' : 'checkbox'}" name="topics[]" value="${topic._id}"${selectedTopicIds && selectedTopicIds.indexOf(topic._id) > -1 ? ' checked="checked"' : ''}><div>${topicName}${topicDescription}</div></label></div></div>`;

              if (childTopics.length) {
                childTopics.forEach((childTopic) => {
                  const childTopicName = charsetEscape(childTopic.name);

                  const childInputType = this.config.confirmAlertRadioButtonTopics || topic.childTopicsRadioButtons ? 'radio' : 'checkbox';

                  topicSelection += `<div class="topic-child" data-parent-topic="${topic._id}" style="margin-left: 20px; font-size: 90%; ${false && topic.defaultUnchecked ? 'display: none;' : ''}"><label> <span>${childTopicName}</span> <input type="${childInputType}" name="topics[]" value="${childTopic._id}" ${selectedTopicIds && selectedTopicIds.indexOf(childTopic._id) > -1 ? ' checked="checked"' : ''}></label></div>`;
                });
              }

              return topicSelection;
            }).join('')
            }</div><button type="button" class="cleverpush-panel-save-topics-button">${this.localization.save}</button>`
              + '</div>';
            setSettingsContent();
          });
        }
      } else {
        setSettingsContent();
      }
    }
  }

  getBrandingLinks() {
    const links = [];

    if (!this.config.hideBranding) {
      if (this.config.cleverpushDomain === 'convertpush.io') {
        links.push(`<a href="https://convertpush.io/" target="_blank" style="${this.config.confirmAlertThemeLinkColor ? (`color: ${this.config.confirmAlertThemeLinkColor} !important`) : ''}">Powered by ConvertPush</a>`);
      } else {
        links.push(`<a href="https://cleverpush.com/" target="_blank" style="${this.config.confirmAlertThemeLinkColor ? (`color: ${this.config.confirmAlertThemeLinkColor} !important`) : ''}">Powered by CleverPush</a>`);
      }
    }
    if (this.config.privacyPolicyUrl) {
      links.push(`<a href="${this.config.privacyPolicyUrl}" target="_blank" style="${this.config.confirmAlertThemeLinkColor ? (`color: ${this.config.confirmAlertThemeLinkColor} !important`) : ''}">${this.config.privacyPolicyText || translate('privacy')}</a>`);
    }
    if (this.config.faqText && this.config.faqUrl) {
      links.push(`<a href="${this.config.faqUrl}" target="_blank" style="${this.config.confirmAlertThemeLinkColor ? (`color: ${this.config.confirmAlertThemeLinkColor} !important`) : ''}">${this.config.faqText || 'FAQ'}</a>`);
    }

    return links.join(' &nbsp;/&nbsp; ');
  }

  saveTopics(e) {
    e.preventDefault();

    if (this.savingTopics) {
      return;
    }
    this.savingTopics = true;

    this.saveTopicsButton.innerHTML += '...';
    this.saveTopicsButton.disabled = true;

    const selectedTopicIds = [];
    this.topicCheckboxes = this.panel.querySelectorAll('.cleverpush-panel-topics input[type="checkbox"], .cleverpush-panel-topics input[type="radio"]');
    if (this.topicCheckboxes && this.topicCheckboxes.length) {
      for (let checkboxIndex = 0; checkboxIndex < this.topicCheckboxes.length; checkboxIndex += 1) {
        const checkbox = this.topicCheckboxes[checkboxIndex];
        if (checkbox.checked) {
          selectedTopicIds.push(checkbox.value);
        }
      }
    }

    const resetButton = (success) => {
      this.saveTopicsButton.disabled = false;
      if (success) {
        this.saveTopicsButton.innerHTML = `<span>${this.localization.successfulSave}</span>`;
        setTimeout(() => {
          this.saveTopicsButton.innerHTML = this.localization.save;
          this.savingTopics = false;
        }, 2000);
      } else {
        this.saveTopicsButton.innerHTML = this.localization.save;
        this.savingTopics = false;
      }
    };

    this.subscriptionManager.setTopics(selectedTopicIds).then(() => {
      this.subscriptionManager.sync().then(() => {
        resetButton(true);
      }).catch(() => {
        resetButton(false);
      });
    }).catch(() => {
      resetButton(false);
    });
  }
}
